import { RepairRequest, RepairStatus } from '../types/firestore';

export type RepairStatusBadgeVariant =
  | 'neutral'
  | 'info'
  | 'warning'
  | 'primary'
  | 'accent'
  | 'success'
  | 'error';

// "client" oznacza tu requester-side runtime actor, czyli właściciela zgłoszenia.
// Shared detail host mapuje do tego runtime aktora zarówno client, jak i serviceAdmin requester-side.
export type RepairRequestWorkflowActor = 'client' | 'service';
export type RepairRequestWorkflowTurn = RepairRequestWorkflowActor | 'none';
export type RepairRequestOrientationKind = 'action' | 'waiting' | 'informational' | 'terminal';

export interface RepairRequestOrientation {
  actor: RepairRequestWorkflowActor;
  stageLabel: string;
  nextActionOwner: RepairRequestNextActionOwner;
  nextActorLabel: string;
  nextStep: string;
  currentActorOwnsWorkflowAction: boolean;
  kind: RepairRequestOrientationKind;
}

export interface RepairRequestStatusMeta {
  status: RepairStatus;
  label: string;
  businessMeaning: string;
  badgeVariant: RepairStatusBadgeVariant;
  isTerminal: boolean;
  requiresClientAction: boolean;
  requiresServiceAction: boolean;
}

export interface RepairRequestWorkflowAction {
  id: string;
  actor: RepairRequestWorkflowActor;
  label: string;
  nextStatus: RepairStatus;
  description: string;
  isDestructive?: boolean;
}

interface RepairRequestTransitionRule {
  from: RepairStatus;
  action: RepairRequestWorkflowAction;
}

export interface RepairRequestProcessStep {
  id: 'submitted' | 'quote' | 'decision' | 'repair' | 'completed';
  label: string;
  description: string;
}

export interface RepairRequestProcessProgress {
  currentStepIndex: number;
  completedStepCount: number;
  isTerminal: boolean;
  totalStepCount: number;
}

export type ClientRequestListBucket = 'needsAction' | 'active' | 'waitingForService' | 'completed';

export interface ClientRequestBuckets {
  all: RepairRequest[];
  needsAction: RepairRequest[];
  active: RepairRequest[];
  waitingForService: RepairRequest[];
  completed: RepairRequest[];
}

export interface ServiceDashboardBuckets {
  all: RepairRequest[];
  requiresServiceAction: RepairRequest[];
  newRequests: RepairRequest[];
  waitingForClient: RepairRequest[];
  inProgress: RepairRequest[];
  completed: RepairRequest[];
  recentlyCompleted: RepairRequest[];
  stale: RepairRequest[];
  active: RepairRequest[];
}

export type ServiceDashboardBucket = 'reaction' | 'inProgress' | 'history';
export type ServiceDashboardTone = 'danger' | 'warning' | 'success' | 'neutral';
export type RepairRequestNextActionOwner = 'service' | 'client' | 'system';

export interface ServiceDashboardStatusMeta {
  status: RepairStatus;
  statusLabel: string;
  bucket: ServiceDashboardBucket;
  bucketLabel: string;
  tone: ServiceDashboardTone;
  isHistory: boolean;
  isCancelled: boolean;
}

export interface ServiceDashboardOverview {
  active: RepairRequest[];
  history: RepairRequest[];
  reaction: RepairRequest[];
  inProgress: RepairRequest[];
  staleActive: RepairRequest[];
  counts: Record<ServiceDashboardBucket, number>;
}

export interface RepairStatusMatrixEntry {
  status: RepairStatus;
  label: string;
  businessMeaning: string;
  badgeVariant: RepairStatusBadgeVariant;
  isTerminal: boolean;
  requiresClientAction: boolean;
  requiresServiceAction: boolean;
  serviceDashboardBucket: ServiceDashboardBucket;
  serviceDashboardTone: ServiceDashboardTone;
  isCancelledHistory: boolean;
  stageLabel: string;
  nextActionOwner: RepairRequestNextActionOwner;
  listHintByActor: Record<RepairRequestWorkflowActor, string>;
  nextStepByActor: Record<RepairRequestWorkflowActor, string>;
}

export const REPAIR_STATUS_ORDER: RepairStatus[] = [
  RepairStatus.NEW,
  RepairStatus.PRELIMINARY_QUOTED,
  RepairStatus.AWAITING_FULFILLMENT,
  RepairStatus.DIAGNOSIS_IN_PROGRESS,
  RepairStatus.FINAL_QUOTE_READY,
  RepairStatus.FINAL_QUOTE_ACCEPTED,
  RepairStatus.FINAL_QUOTE_REJECTED,
  RepairStatus.REPAIR_IN_PROGRESS,
  RepairStatus.REPAIR_COMPLETED,
  RepairStatus.AWAITING_CASH_SETTLEMENT,
  RepairStatus.PENDING_QUOTE,
  RepairStatus.QUOTED,
  RepairStatus.ACCEPTED,
  RepairStatus.IN_PROGRESS,
  RepairStatus.PENDING_PAYMENT,
  RepairStatus.COMPLETED,
  RepairStatus.CANCELLED_BY_CLIENT,
  RepairStatus.CANCELLED_BY_SERVICE,
  RepairStatus.PRELIMINARY_REJECTED,
  RepairStatus.REJECTED,
];

export const REPAIR_STATUS_MATRIX: Record<RepairStatus, RepairStatusMatrixEntry> = {
  [RepairStatus.NEW]: {
    status: RepairStatus.NEW,
    label: 'Nowe',
    businessMeaning: 'Nowe zgłoszenie czeka na decyzję serwisu.',
    badgeVariant: 'info',
    isTerminal: false,
    requiresClientAction: false,
    requiresServiceAction: true,
    serviceDashboardBucket: 'reaction',
    serviceDashboardTone: 'danger',
    isCancelledHistory: false,
    stageLabel: 'Przyjęcie i wycena',
    nextActionOwner: 'service',
    listHintByActor: {
      service: 'Nowe zgłoszenie wymaga Twojej decyzji.',
      client: 'Nowe zgłoszenie czeka na decyzję serwisu.',
    },
    nextStepByActor: {
      service: 'Zweryfikuj zgłoszenie i rozpocznij wycenę.',
      client: 'Serwis analizuje zgłoszenie i przygotowuje pierwszy krok.',
    },
  },
  [RepairStatus.PRELIMINARY_QUOTED]: {
    status: RepairStatus.PRELIMINARY_QUOTED,
    label: 'Wycena wstępna wysłana',
    businessMeaning: 'Wycena wstępna została wysłana i czeka na decyzję właściciela zgłoszenia.',
    badgeVariant: 'warning',
    isTerminal: false,
    requiresClientAction: true,
    requiresServiceAction: false,
    serviceDashboardBucket: 'inProgress',
    serviceDashboardTone: 'warning',
    isCancelledHistory: false,
    stageLabel: 'Decyzja o wycenie wstępnej',
    nextActionOwner: 'client',
    listHintByActor: {
      service: 'Wycena wstępna czeka na decyzję właściciela zgłoszenia.',
      client: 'Serwis wysłał wycenę wstępną. Podejmij decyzję.',
    },
    nextStepByActor: {
      service: 'Czekaj na decyzję właściciela zgłoszenia dotyczącą wyceny wstępnej.',
      client: 'Zapoznaj się z wyceną wstępną i podejmij decyzję.',
    },
  },
  [RepairStatus.AWAITING_FULFILLMENT]: {
    status: RepairStatus.AWAITING_FULFILLMENT,
    label: 'Oczekuje na realizację przekazania',
    businessMeaning:
      'Wycena wstępna została zaakceptowana. Sprawa czeka na dostarczenie, odbiór albo wizytę.',
    badgeVariant: 'primary',
    isTerminal: false,
    requiresClientAction: false,
    requiresServiceAction: true,
    serviceDashboardBucket: 'inProgress',
    serviceDashboardTone: 'warning',
    isCancelledHistory: false,
    stageLabel: 'Przekazanie sprzętu',
    nextActionOwner: 'service',
    listHintByActor: {
      service: 'Potwierdź przekazanie zgodnie z wybranym sposobem realizacji.',
      client: 'Zrealizuj uzgodniony sposób przekazania; serwis potwierdzi je w systemie.',
    },
    nextStepByActor: {
      service: 'Potwierdź przekazanie zgodnie z wybranym sposobem realizacji.',
      client: 'Zrealizuj uzgodniony sposób przekazania; serwis potwierdzi je w systemie.',
    },
  },
  [RepairStatus.DIAGNOSIS_IN_PROGRESS]: {
    status: RepairStatus.DIAGNOSIS_IN_PROGRESS,
    label: 'Diagnoza w toku',
    businessMeaning:
      'Serwis potwierdził przyjęcie, odbiór albo przyjazd i prowadzi diagnozę sprzętu.',
    badgeVariant: 'primary',
    isTerminal: false,
    requiresClientAction: false,
    requiresServiceAction: true,
    serviceDashboardBucket: 'reaction',
    serviceDashboardTone: 'danger',
    isCancelledHistory: false,
    stageLabel: 'Diagnoza',
    nextActionOwner: 'service',
    listHintByActor: {
      service: 'Diagnoza w toku. Przygotuj finalną wycenę.',
      client: 'Serwis potwierdził przekazanie i prowadzi diagnozę.',
    },
    nextStepByActor: {
      service: 'Zapisz wynik diagnozy i finalną wycenę.',
      client: 'Czekaj na informację z serwisu po diagnozie.',
    },
  },
  [RepairStatus.FINAL_QUOTE_READY]: {
    status: RepairStatus.FINAL_QUOTE_READY,
    label: 'Finalna wycena gotowa',
    businessMeaning: 'Serwis zakończył diagnozę i wysłał finalną wycenę do decyzji.',
    badgeVariant: 'warning',
    isTerminal: false,
    requiresClientAction: true,
    requiresServiceAction: false,
    serviceDashboardBucket: 'inProgress',
    serviceDashboardTone: 'warning',
    isCancelledHistory: false,
    stageLabel: 'Decyzja o finalnej wycenie',
    nextActionOwner: 'client',
    listHintByActor: {
      service: 'Finalna wycena czeka na decyzję właściciela zgłoszenia.',
      client: 'Serwis zakończył diagnozę. Podejmij decyzję o finalnej wycenie.',
    },
    nextStepByActor: {
      service: 'Czekaj na decyzję właściciela zgłoszenia.',
      client: 'Zapoznaj się z diagnozą i finalną wyceną, potem podejmij decyzję.',
    },
  },
  [RepairStatus.FINAL_QUOTE_ACCEPTED]: {
    status: RepairStatus.FINAL_QUOTE_ACCEPTED,
    label: 'Finalna wycena zaakceptowana',
    businessMeaning: 'Właściciel zgłoszenia zaakceptował finalną wycenę naprawy.',
    badgeVariant: 'primary',
    isTerminal: false,
    requiresClientAction: false,
    requiresServiceAction: true,
    serviceDashboardBucket: 'reaction',
    serviceDashboardTone: 'danger',
    isCancelledHistory: false,
    stageLabel: 'Naprawa zaakceptowana',
    nextActionOwner: 'service',
    listHintByActor: {
      service: 'Właściciel zgłoszenia zaakceptował finalną wycenę.',
      client: 'Finalna wycena została zaakceptowana. Serwis może prowadzić naprawę.',
    },
    nextStepByActor: {
      service: 'Rozpocznij naprawę.',
      client: 'Serwis powinien rozpocząć naprawę.',
    },
  },
  [RepairStatus.REPAIR_IN_PROGRESS]: {
    status: RepairStatus.REPAIR_IN_PROGRESS,
    label: 'Naprawa w toku',
    businessMeaning: 'Serwis realizuje naprawę po zaakceptowanej finalnej wycenie.',
    badgeVariant: 'primary',
    isTerminal: false,
    requiresClientAction: false,
    requiresServiceAction: true,
    serviceDashboardBucket: 'reaction',
    serviceDashboardTone: 'danger',
    isCancelledHistory: false,
    stageLabel: 'Naprawa',
    nextActionOwner: 'service',
    listHintByActor: {
      service: 'Naprawa trwa. Po wykonaniu prac oznacz ją jako zakończoną.',
      client: 'Serwis prowadzi naprawę.',
    },
    nextStepByActor: {
      service: 'Oznacz naprawę jako wykonaną po zakończeniu prac.',
      client: 'Czekaj na informację o zakończeniu prac.',
    },
  },
  [RepairStatus.REPAIR_COMPLETED]: {
    status: RepairStatus.REPAIR_COMPLETED,
    label: 'Naprawa wykonana',
    businessMeaning: 'Prace naprawcze zostały zakończone i czekają na rozliczenie.',
    badgeVariant: 'accent',
    isTerminal: false,
    requiresClientAction: false,
    requiresServiceAction: true,
    serviceDashboardBucket: 'reaction',
    serviceDashboardTone: 'danger',
    isCancelledHistory: false,
    stageLabel: 'Rozliczenie',
    nextActionOwner: 'service',
    listHintByActor: {
      service: 'Naprawa wykonana. Przygotuj rozliczenie gotówką lub bezpośrednio.',
      client: 'Naprawa została wykonana i czeka na rozliczenie.',
    },
    nextStepByActor: {
      service: 'Przygotuj rozliczenie gotówką lub bezpośrednio.',
      client: 'Ustal z serwisem odbiór lub zwrot oraz rozliczenie.',
    },
  },
  [RepairStatus.AWAITING_CASH_SETTLEMENT]: {
    status: RepairStatus.AWAITING_CASH_SETTLEMENT,
    label: 'Oczekuje rozliczenia',
    businessMeaning:
      'Naprawa jest wykonana i czeka na potwierdzenie rozliczenia gotówką lub bezpośrednio.',
    badgeVariant: 'accent',
    isTerminal: false,
    requiresClientAction: false,
    requiresServiceAction: true,
    serviceDashboardBucket: 'reaction',
    serviceDashboardTone: 'danger',
    isCancelledHistory: false,
    stageLabel: 'Rozliczenie',
    nextActionOwner: 'service',
    listHintByActor: {
      service: 'Potwierdź rozliczenie i zakończ zlecenie.',
      client: 'Rozlicz naprawę z serwisem gotówką lub bezpośrednio.',
    },
    nextStepByActor: {
      service: 'Potwierdź rozliczenie i zakończ zlecenie.',
      client: 'Rozliczenie odbywa się poza płatnościami online.',
    },
  },
  [RepairStatus.FINAL_QUOTE_REJECTED]: {
    status: RepairStatus.FINAL_QUOTE_REJECTED,
    label: 'Finalna wycena odrzucona',
    businessMeaning:
      'Właściciel zgłoszenia odrzucił finalną wycenę. Naprawa nie została zaakceptowana.',
    badgeVariant: 'error',
    isTerminal: true,
    requiresClientAction: false,
    requiresServiceAction: false,
    serviceDashboardBucket: 'history',
    serviceDashboardTone: 'neutral',
    isCancelledHistory: true,
    stageLabel: 'Naprawa niezaakceptowana',
    nextActionOwner: 'system',
    listHintByActor: {
      service: 'Finalna wycena została odrzucona. Naprawa nie została zaakceptowana.',
      client: 'Finalna wycena została odrzucona. Naprawa nie została zaakceptowana.',
    },
    nextStepByActor: {
      service: 'Dalszy zwrot lub rozliczenie diagnozy wymaga osobnego procesu.',
      client:
        'Naprawa nie została zaakceptowana. Zwrot lub rozliczenie diagnozy ustal poza tym etapem.',
    },
  },
  [RepairStatus.PENDING_QUOTE]: {
    status: RepairStatus.PENDING_QUOTE,
    label: 'Oczekuje wyceny',
    businessMeaning: 'Serwis analizuje zgłoszenie i przygotowuje wycenę.',
    badgeVariant: 'warning',
    isTerminal: false,
    requiresClientAction: false,
    requiresServiceAction: true,
    serviceDashboardBucket: 'reaction',
    serviceDashboardTone: 'danger',
    isCancelledHistory: false,
    stageLabel: 'Przyjęcie i wycena',
    nextActionOwner: 'service',
    listHintByActor: {
      service: 'Przygotuj wycenę i odeślij właścicielowi zgłoszenia.',
      client: 'Serwis przygotowuje wycenę Twojej sprawy.',
    },
    nextStepByActor: {
      service: 'Przygotuj ofertę i wyślij wycenę właścicielowi zgłoszenia.',
      client: 'Czekaj na wycenę od serwisu lub dopytaj w komunikacji.',
    },
  },
  [RepairStatus.QUOTED]: {
    status: RepairStatus.QUOTED,
    label: 'Oczekuje decyzji właściciela zgłoszenia',
    businessMeaning:
      'Wycena została wysłana i czeka na akceptację lub odrzucenie przez właściciela zgłoszenia.',
    badgeVariant: 'warning',
    isTerminal: false,
    requiresClientAction: true,
    requiresServiceAction: false,
    serviceDashboardBucket: 'inProgress',
    serviceDashboardTone: 'warning',
    isCancelledHistory: false,
    stageLabel: 'Decyzja właściciela zgłoszenia',
    nextActionOwner: 'client',
    listHintByActor: {
      service: 'Wycena została wysłana. Czekamy na decyzję właściciela zgłoszenia.',
      client: 'Serwis wysłał wycenę. Podejmij decyzję.',
    },
    nextStepByActor: {
      service: 'Czekaj na decyzję właściciela zgłoszenia lub przypomnij się wiadomością.',
      client: 'Zapoznaj się z wyceną i podejmij decyzję.',
    },
  },
  [RepairStatus.ACCEPTED]: {
    status: RepairStatus.ACCEPTED,
    label: 'Wycena zaakceptowana',
    businessMeaning: 'Właściciel zgłoszenia zaakceptował wycenę. Serwis może rozpocząć naprawę.',
    badgeVariant: 'primary',
    isTerminal: false,
    requiresClientAction: false,
    requiresServiceAction: true,
    serviceDashboardBucket: 'reaction',
    serviceDashboardTone: 'danger',
    isCancelledHistory: false,
    stageLabel: 'Gotowe do realizacji',
    nextActionOwner: 'service',
    listHintByActor: {
      service: 'Właściciel zgłoszenia zaakceptował wycenę. Możesz rozpocząć naprawę.',
      client: 'Wycena została zaakceptowana. Serwis przygotowuje rozpoczęcie naprawy.',
    },
    nextStepByActor: {
      service: 'Rozpocznij realizację naprawy.',
      client: 'Serwis powinien rozpocząć realizację naprawy.',
    },
  },
  [RepairStatus.IN_PROGRESS]: {
    status: RepairStatus.IN_PROGRESS,
    label: 'W trakcie naprawy',
    businessMeaning: 'Naprawa jest realizowana przez serwis.',
    badgeVariant: 'primary',
    isTerminal: false,
    requiresClientAction: false,
    requiresServiceAction: true,
    serviceDashboardBucket: 'inProgress',
    serviceDashboardTone: 'warning',
    isCancelledHistory: false,
    stageLabel: 'W realizacji',
    nextActionOwner: 'service',
    listHintByActor: {
      service: 'Kontynuuj naprawę i aktualizuj postęp.',
      client: 'Naprawa jest realizowana przez serwis.',
    },
    nextStepByActor: {
      service: 'Aktualizuj postęp naprawy i przygotuj etap rozliczenia.',
      client: 'Czekaj na postęp serwisu lub dopytaj w komunikacji.',
    },
  },
  [RepairStatus.PENDING_PAYMENT]: {
    status: RepairStatus.PENDING_PAYMENT,
    label: 'Oczekuje na płatność',
    businessMeaning: 'Naprawa zakończona technicznie. Oczekiwanie na rozliczenie płatności.',
    badgeVariant: 'accent',
    isTerminal: false,
    requiresClientAction: true,
    requiresServiceAction: true,
    serviceDashboardBucket: 'inProgress',
    serviceDashboardTone: 'warning',
    isCancelledHistory: false,
    stageLabel: 'Rozliczenie',
    nextActionOwner: 'client',
    listHintByActor: {
      service: 'Czeka na rozliczenie właściciela zgłoszenia.',
      client: 'Rozlicz naprawę z serwisem, aby domknąć sprawę.',
    },
    nextStepByActor: {
      service: 'Po rozliczeniu potwierdź zakończenie zlecenia.',
      client: 'Skontaktuj się z serwisem i domknij rozliczenie.',
    },
  },
  [RepairStatus.COMPLETED]: {
    status: RepairStatus.COMPLETED,
    label: 'Zakończone',
    businessMeaning: 'Proces naprawy został zakończony.',
    badgeVariant: 'success',
    isTerminal: true,
    requiresClientAction: false,
    requiresServiceAction: false,
    serviceDashboardBucket: 'history',
    serviceDashboardTone: 'success',
    isCancelledHistory: false,
    stageLabel: 'Zamknięte',
    nextActionOwner: 'system',
    listHintByActor: {
      service: 'Sprawa została zamknięta.',
      client: 'Sprawa została zamknięta.',
    },
    nextStepByActor: {
      service: 'Sprawa została zamknięta.',
      client: 'Sprawa została zakończona.',
    },
  },
  [RepairStatus.CANCELLED_BY_CLIENT]: {
    status: RepairStatus.CANCELLED_BY_CLIENT,
    label: 'Anulowane przez właściciela zgłoszenia',
    businessMeaning: 'Właściciel zgłoszenia anulował zgłoszenie.',
    badgeVariant: 'error',
    isTerminal: true,
    requiresClientAction: false,
    requiresServiceAction: false,
    serviceDashboardBucket: 'history',
    serviceDashboardTone: 'neutral',
    isCancelledHistory: true,
    stageLabel: 'Zamknięte',
    nextActionOwner: 'system',
    listHintByActor: {
      service: 'Sprawa została anulowana przez właściciela zgłoszenia.',
      client: 'Sprawa została anulowana.',
    },
    nextStepByActor: {
      service: 'Sprawa została zamknięta.',
      client: 'Sprawa została zamknięta.',
    },
  },
  [RepairStatus.CANCELLED_BY_SERVICE]: {
    status: RepairStatus.CANCELLED_BY_SERVICE,
    label: 'Zamknięte bez naprawy',
    businessMeaning: 'Serwis zamknął sprawę bez naprawy.',
    badgeVariant: 'error',
    isTerminal: true,
    requiresClientAction: false,
    requiresServiceAction: false,
    serviceDashboardBucket: 'history',
    serviceDashboardTone: 'neutral',
    isCancelledHistory: true,
    stageLabel: 'Zamknięte',
    nextActionOwner: 'system',
    listHintByActor: {
      service: 'Sprawa została zamknięta przez serwis.',
      client: 'Serwis nie podejmie tej naprawy.',
    },
    nextStepByActor: {
      service: 'Sprawa została zamknięta przez serwis.',
      client: 'Serwis zamknął sprawę bez naprawy.',
    },
  },
  [RepairStatus.PRELIMINARY_REJECTED]: {
    status: RepairStatus.PRELIMINARY_REJECTED,
    label: 'Wycena wstępna odrzucona',
    businessMeaning: 'Właściciel zgłoszenia odrzucił wycenę wstępną.',
    badgeVariant: 'error',
    isTerminal: true,
    requiresClientAction: false,
    requiresServiceAction: false,
    serviceDashboardBucket: 'history',
    serviceDashboardTone: 'neutral',
    isCancelledHistory: true,
    stageLabel: 'Zamknięte',
    nextActionOwner: 'system',
    listHintByActor: {
      service: 'Wycena wstępna została odrzucona.',
      client: 'Wycena wstępna została odrzucona.',
    },
    nextStepByActor: {
      service: 'Sprawa została zamknięta.',
      client: 'Sprawa została zamknięta.',
    },
  },
  [RepairStatus.REJECTED]: {
    status: RepairStatus.REJECTED,
    label: 'Odrzucone',
    businessMeaning: 'Zlecenie lub oferta zostały odrzucone.',
    badgeVariant: 'error',
    isTerminal: true,
    requiresClientAction: false,
    requiresServiceAction: false,
    serviceDashboardBucket: 'history',
    serviceDashboardTone: 'neutral',
    isCancelledHistory: true,
    stageLabel: 'Zamknięte',
    nextActionOwner: 'system',
    listHintByActor: {
      service: 'Sprawa została odrzucona.',
      client: 'Sprawa została odrzucona.',
    },
    nextStepByActor: {
      service: 'Sprawa została zamknięta.',
      client: 'Sprawa została zamknięta.',
    },
  },
};

const TRANSITION_RULES: RepairRequestTransitionRule[] = [
  {
    from: RepairStatus.FINAL_QUOTE_ACCEPTED,
    action: {
      id: 'service_start_v2_repair',
      actor: 'service',
      label: 'Rozpocznij naprawę',
      nextStatus: RepairStatus.REPAIR_IN_PROGRESS,
      description: 'Serwis rozpoczyna naprawę po zaakceptowanej finalnej wycenie.',
    },
  },
  {
    from: RepairStatus.REPAIR_IN_PROGRESS,
    action: {
      id: 'service_mark_v2_repair_completed',
      actor: 'service',
      label: 'Oznacz naprawę jako wykonaną',
      nextStatus: RepairStatus.REPAIR_COMPLETED,
      description: 'Serwis zapisuje podsumowanie wykonanej naprawy.',
    },
  },
  {
    from: RepairStatus.REPAIR_COMPLETED,
    action: {
      id: 'service_prepare_cash_settlement',
      actor: 'service',
      label: 'Przygotuj rozliczenie',
      nextStatus: RepairStatus.AWAITING_CASH_SETTLEMENT,
      description: 'Serwis przygotowuje rozliczenie gotówką lub bezpośrednio.',
    },
  },
  {
    from: RepairStatus.AWAITING_CASH_SETTLEMENT,
    action: {
      id: 'service_confirm_cash_settlement',
      actor: 'service',
      label: 'Potwierdź rozliczenie i zakończ',
      nextStatus: RepairStatus.COMPLETED,
      description: 'Serwis potwierdza rozliczenie gotówką lub bezpośrednio i zamyka zlecenie.',
    },
  },
  {
    from: RepairStatus.NEW,
    action: {
      id: 'service_start_quote',
      actor: 'service',
      label: 'Rozpocznij wycenę',
      nextStatus: RepairStatus.PENDING_QUOTE,
      description: 'Serwis rozpoczyna analizę i przygotowanie wyceny.',
    },
  },
  {
    from: RepairStatus.NEW,
    action: {
      id: 'service_reject_request',
      actor: 'service',
      label: 'Odrzuć zlecenie',
      nextStatus: RepairStatus.REJECTED,
      description: 'Serwis odrzuca realizację zgłoszenia.',
      isDestructive: true,
    },
  },
  {
    from: RepairStatus.NEW,
    action: {
      id: 'service_cancel_v2_request',
      actor: 'service',
      label: 'Nie podejmę naprawy',
      nextStatus: RepairStatus.CANCELLED_BY_SERVICE,
      description: 'Serwis zamyka sprawę bez naprawy z powodem widocznym dla klienta.',
      isDestructive: true,
    },
  },
  {
    from: RepairStatus.NEW,
    action: {
      id: 'client_cancel_new',
      actor: 'client',
      label: 'Anuluj zgłoszenie',
      nextStatus: RepairStatus.CANCELLED_BY_CLIENT,
      description: 'Klient anuluje zgłoszenie przed rozpoczęciem obsługi.',
      isDestructive: true,
    },
  },
  {
    from: RepairStatus.PENDING_QUOTE,
    action: {
      id: 'service_send_quote',
      actor: 'service',
      label: 'Wyślij wycenę',
      nextStatus: RepairStatus.QUOTED,
      description: 'Serwis przekazuje klientowi wycenę.',
    },
  },
  {
    from: RepairStatus.PENDING_QUOTE,
    action: {
      id: 'service_reject_after_analysis',
      actor: 'service',
      label: 'Odrzuć zlecenie',
      nextStatus: RepairStatus.REJECTED,
      description: 'Serwis odrzuca zgłoszenie po analizie.',
      isDestructive: true,
    },
  },
  {
    from: RepairStatus.PENDING_QUOTE,
    action: {
      id: 'client_cancel_during_quote',
      actor: 'client',
      label: 'Anuluj zgłoszenie',
      nextStatus: RepairStatus.CANCELLED_BY_CLIENT,
      description: 'Klient anuluje zgłoszenie podczas przygotowywania wyceny.',
      isDestructive: true,
    },
  },
  {
    from: RepairStatus.AWAITING_FULFILLMENT,
    action: {
      id: 'service_cancel_v2_request',
      actor: 'service',
      label: 'Nie podejmę naprawy',
      nextStatus: RepairStatus.CANCELLED_BY_SERVICE,
      description: 'Serwis zamyka sprawę bez naprawy z powodem widocznym dla klienta.',
      isDestructive: true,
    },
  },
  {
    from: RepairStatus.DIAGNOSIS_IN_PROGRESS,
    action: {
      id: 'service_cancel_v2_request',
      actor: 'service',
      label: 'Nie podejmę naprawy',
      nextStatus: RepairStatus.CANCELLED_BY_SERVICE,
      description: 'Serwis zamyka sprawę bez naprawy z powodem widocznym dla klienta.',
      isDestructive: true,
    },
  },
  {
    from: RepairStatus.QUOTED,
    action: {
      id: 'client_accept_quote',
      actor: 'client',
      label: 'Zaakceptuj wycenę',
      nextStatus: RepairStatus.ACCEPTED,
      description: 'Klient akceptuje warunki i zlecenie przechodzi do realizacji.',
    },
  },
  {
    from: RepairStatus.QUOTED,
    action: {
      id: 'client_reject_quote',
      actor: 'client',
      label: 'Odrzuć wycenę',
      nextStatus: RepairStatus.REJECTED,
      description: 'Klient odrzuca przedstawioną wycenę.',
      isDestructive: true,
    },
  },
  {
    from: RepairStatus.ACCEPTED,
    action: {
      id: 'service_start_repair',
      actor: 'service',
      label: 'Rozpocznij naprawę',
      nextStatus: RepairStatus.IN_PROGRESS,
      description: 'Serwis rozpoczyna realizację zlecenia.',
    },
  },
  {
    from: RepairStatus.ACCEPTED,
    action: {
      id: 'client_cancel_after_acceptance',
      actor: 'client',
      label: 'Anuluj zgłoszenie',
      nextStatus: RepairStatus.CANCELLED_BY_CLIENT,
      description: 'Klient anuluje przed rozpoczęciem naprawy.',
      isDestructive: true,
    },
  },
  {
    from: RepairStatus.ACCEPTED,
    action: {
      id: 'service_cancel_after_acceptance',
      actor: 'service',
      label: 'Anuluj realizację',
      nextStatus: RepairStatus.CANCELLED_BY_SERVICE,
      description: 'Serwis anuluje realizację po akceptacji.',
      isDestructive: true,
    },
  },
  {
    from: RepairStatus.IN_PROGRESS,
    action: {
      id: 'service_mark_pending_payment',
      actor: 'service',
      label: 'Oznacz jako oczekujące na płatność',
      nextStatus: RepairStatus.PENDING_PAYMENT,
      description: 'Serwis oznacza zlecenie jako gotowe do rozliczenia.',
    },
  },
  {
    from: RepairStatus.IN_PROGRESS,
    action: {
      id: 'service_complete_directly',
      actor: 'service',
      label: 'Oznacz jako zakończone',
      nextStatus: RepairStatus.COMPLETED,
      description: 'Serwis kończy zlecenie bez etapu płatności.',
    },
  },
  {
    from: RepairStatus.IN_PROGRESS,
    action: {
      id: 'service_cancel_in_progress',
      actor: 'service',
      label: 'Anuluj realizację',
      nextStatus: RepairStatus.CANCELLED_BY_SERVICE,
      description: 'Serwis przerywa realizację zlecenia.',
      isDestructive: true,
    },
  },
  {
    from: RepairStatus.PENDING_PAYMENT,
    action: {
      id: 'service_complete_after_payment',
      actor: 'service',
      label: 'Potwierdź zakończenie',
      nextStatus: RepairStatus.COMPLETED,
      description: 'Serwis potwierdza zakończenie po rozliczeniu.',
    },
  },
];

const PROCESS_STEPS: RepairRequestProcessStep[] = [
  {
    id: 'submitted',
    label: 'Zgłoszenie',
    description: 'Serwis otrzymał zgłoszenie.',
  },
  {
    id: 'quote',
    label: 'Wycena',
    description: 'Serwis analizuje problem i przygotowuje warunki.',
  },
  {
    id: 'decision',
    label: 'Decyzja',
    description: 'Czekamy na decyzję właściciela zgłoszenia dotyczącą wyceny.',
  },
  {
    id: 'repair',
    label: 'Realizacja',
    description: 'Serwis rozpoczyna lub prowadzi naprawę.',
  },
  {
    id: 'completed',
    label: 'Zakończenie',
    description: 'Sprawa została zamknięta.',
  },
];

const CLIENT_LIST_PRIORITY: ClientRequestListBucket[] = [
  'needsAction',
  'active',
  'waitingForService',
  'completed',
];

type ServiceRequestListBucket =
  | 'requiresServiceAction'
  | 'newRequests'
  | 'inProgress'
  | 'waitingForClient'
  | 'completed';

const SERVICE_LIST_PRIORITY: ServiceRequestListBucket[] = [
  'requiresServiceAction',
  'newRequests',
  'inProgress',
  'waitingForClient',
  'completed',
];

const getActionsForStatus = (status: RepairStatus): RepairRequestWorkflowAction[] =>
  TRANSITION_RULES.filter((rule) => rule.from === status).map((rule) => rule.action);

const FALLBACK_STATUS_META: RepairRequestStatusMeta = {
  status: RepairStatus.NEW,
  label: 'Nieznany status',
  businessMeaning: 'Status zlecenia nie jest rozpoznany przez aktualny workflow.',
  badgeVariant: 'neutral',
  isTerminal: false,
  requiresClientAction: false,
  requiresServiceAction: false,
};

const FALLBACK_STATUS_MATRIX: RepairStatusMatrixEntry = {
  ...FALLBACK_STATUS_META,
  serviceDashboardBucket: 'inProgress',
  serviceDashboardTone: 'warning',
  isCancelledHistory: false,
  stageLabel: 'Proces',
  nextActionOwner: 'system',
  listHintByActor: {
    service: FALLBACK_STATUS_META.businessMeaning,
    client: FALLBACK_STATUS_META.businessMeaning,
  },
  nextStepByActor: {
    service: FALLBACK_STATUS_META.businessMeaning,
    client: FALLBACK_STATUS_META.businessMeaning,
  },
};

export const getRepairStatusMatrix = (status: RepairStatus): RepairStatusMatrixEntry =>
  REPAIR_STATUS_MATRIX[status] || {
    ...FALLBACK_STATUS_MATRIX,
    businessMeaning: `Status zlecenia nie jest rozpoznany przez aktualny workflow (${String(status)}).`,
    listHintByActor: {
      service: `Status zlecenia nie jest rozpoznany przez aktualny workflow (${String(status)}).`,
      client: `Status zlecenia nie jest rozpoznany przez aktualny workflow (${String(status)}).`,
    },
    nextStepByActor: {
      service: `Status zlecenia nie jest rozpoznany przez aktualny workflow (${String(status)}).`,
      client: `Status zlecenia nie jest rozpoznany przez aktualny workflow (${String(status)}).`,
    },
  };

const getRepairRequestNextActorLabel = (owner: RepairRequestNextActionOwner): string => {
  if (owner === 'service') return 'serwis';
  if (owner === 'client') return 'właściciel zgłoszenia';
  return 'system';
};

/**
 * Neutral, actor-relative orientation for the shared V2 presentation surfaces.
 * It only describes the canonical status matrix; workflow mutations remain in
 * the existing action/callable paths.
 */
export const getRepairRequestOrientation = (
  status: RepairStatus,
  actor: RepairRequestWorkflowActor
): RepairRequestOrientation => {
  const matrix = getRepairStatusMatrix(status);
  const currentActorOwnsWorkflowAction = matrix.nextActionOwner === actor;
  const kind: RepairRequestOrientationKind = matrix.isTerminal
    ? 'terminal'
    : matrix.nextActionOwner === 'system'
      ? 'informational'
      : currentActorOwnsWorkflowAction
        ? 'action'
        : 'waiting';

  return {
    actor,
    stageLabel: matrix.stageLabel,
    nextActionOwner: matrix.nextActionOwner,
    nextActorLabel: getRepairRequestNextActorLabel(matrix.nextActionOwner),
    nextStep: matrix.nextStepByActor[actor],
    currentActorOwnsWorkflowAction,
    kind,
  };
};

export const getRepairRequestStatusMeta = (status: RepairStatus): RepairRequestStatusMeta => {
  const matrix = getRepairStatusMatrix(status);
  return {
    status: matrix.status,
    label: matrix.label,
    businessMeaning: matrix.businessMeaning,
    badgeVariant: matrix.badgeVariant,
    isTerminal: matrix.isTerminal,
    requiresClientAction: matrix.requiresClientAction,
    requiresServiceAction: matrix.requiresServiceAction,
  };
};

export const getRepairRequestStatusLabel = (status: RepairStatus): string =>
  getRepairRequestStatusMeta(status).label;

export const getRepairRequestStatusVariant = (status: RepairStatus): RepairStatusBadgeVariant =>
  getRepairRequestStatusMeta(status).badgeVariant;

export const isTerminalRepairRequestStatus = (status: RepairStatus): boolean =>
  getRepairRequestStatusMeta(status).isTerminal;

export const isActiveRepairRequestStatus = (status: RepairStatus): boolean =>
  !isTerminalRepairRequestStatus(status);

export const requiresClientAction = (status: RepairStatus): boolean =>
  getRepairRequestStatusMeta(status).requiresClientAction;

export const requiresServiceAction = (status: RepairStatus): boolean =>
  getRepairRequestStatusMeta(status).requiresServiceAction;

export const getRepairRequestWorkflowTurn = (status: RepairStatus): RepairRequestWorkflowTurn => {
  const matrix = getRepairStatusMatrix(status);
  if (matrix.isTerminal) return 'none';
  if (matrix.nextActionOwner === 'client') return 'client';
  if (matrix.nextActionOwner === 'service') return 'service';
  return 'none';
};

export const getRepairRequestWorkflowBlocker = (
  status: RepairStatus,
  actor: RepairRequestWorkflowActor
): string | null => {
  const meta = getRepairRequestStatusMeta(status);
  if (meta.isTerminal) {
    return 'Sprawa jest zamknięta i nie wymaga dalszych działań.';
  }

  const turn = getRepairRequestWorkflowTurn(status);
  if (turn === 'none' || turn === actor) {
    return null;
  }

  if (status === RepairStatus.QUOTED && actor === 'service') {
    return 'Wycena została wysłana. Czekamy na decyzję właściciela zgłoszenia przed rozpoczęciem naprawy.';
  }

  return actor === 'service'
    ? 'Dalszy postęp zależy teraz od decyzji właściciela zgłoszenia.'
    : 'Dalszy postęp zależy teraz od działania serwisu.';
};

export const getRepairRequestStageLabel = (status: RepairStatus): string => {
  return getRepairStatusMatrix(status).stageLabel;
};

export const getRepairRequestListHint = (
  status: RepairStatus,
  actor: RepairRequestWorkflowActor
): string => getRepairStatusMatrix(status).listHintByActor[actor];

export const getRepairRequestProcessSteps = (): RepairRequestProcessStep[] => PROCESS_STEPS;

const getProcessStepIndexByStatus = (status: RepairStatus): number => {
  switch (status) {
    case RepairStatus.NEW:
      return 0;
    case RepairStatus.PENDING_QUOTE:
      return 1;
    case RepairStatus.PRELIMINARY_QUOTED:
    case RepairStatus.QUOTED:
      return 2;
    case RepairStatus.AWAITING_FULFILLMENT:
    case RepairStatus.ACCEPTED:
    case RepairStatus.DIAGNOSIS_IN_PROGRESS:
    case RepairStatus.FINAL_QUOTE_READY:
    case RepairStatus.FINAL_QUOTE_ACCEPTED:
    case RepairStatus.REPAIR_IN_PROGRESS:
    case RepairStatus.REPAIR_COMPLETED:
    case RepairStatus.AWAITING_CASH_SETTLEMENT:
    case RepairStatus.IN_PROGRESS:
    case RepairStatus.PENDING_PAYMENT:
      return 3;
    case RepairStatus.COMPLETED:
    case RepairStatus.CANCELLED_BY_CLIENT:
    case RepairStatus.CANCELLED_BY_SERVICE:
    case RepairStatus.PRELIMINARY_REJECTED:
    case RepairStatus.FINAL_QUOTE_REJECTED:
    case RepairStatus.REJECTED:
      return 4;
    default:
      return 0;
  }
};

export const getRepairRequestProcessProgress = (
  status: RepairStatus
): RepairRequestProcessProgress => {
  const totalStepCount = PROCESS_STEPS.length;
  const currentStepIndex = Math.min(
    Math.max(getProcessStepIndexByStatus(status), 0),
    totalStepCount - 1
  );
  const isTerminal = isTerminalRepairRequestStatus(status);
  const completedStepCount = isTerminal ? totalStepCount : currentStepIndex;

  return {
    currentStepIndex,
    completedStepCount,
    isTerminal,
    totalStepCount,
  };
};

export const getRepairRequestProcessStepIndex = (status: RepairStatus): number =>
  getRepairRequestProcessProgress(status).currentStepIndex;

/**
 * The five-step process remains the canonical progress model, but these two
 * V2 states need their exact workflow stage in the current-step presentation.
 */
export const getRepairRequestProcessStepLabel = (status: RepairStatus): string => {
  if (status === RepairStatus.AWAITING_FULFILLMENT || status === RepairStatus.FINAL_QUOTE_READY) {
    return getRepairStatusMatrix(status).stageLabel;
  }

  const progress = getRepairRequestProcessProgress(status);
  return (
    PROCESS_STEPS[progress.currentStepIndex]?.label || getRepairStatusMatrix(status).stageLabel
  );
};

export const getPrimaryWorkflowAction = (
  status: RepairStatus,
  actor: RepairRequestWorkflowActor
): RepairRequestWorkflowAction | null => {
  const actions =
    actor === 'client' ? getAvailableClientActions(status) : getAvailableServiceActions(status);
  if (actions.length === 0) return null;

  const nonDestructive = actions.find((action) => !action.isDestructive);
  return nonDestructive || actions[0];
};

export const getAvailableClientActions = (status: RepairStatus): RepairRequestWorkflowAction[] =>
  getActionsForStatus(status).filter((action) => action.actor === 'client');

export const getAvailableServiceActions = (status: RepairStatus): RepairRequestWorkflowAction[] =>
  getActionsForStatus(status).filter((action) => action.actor === 'service');

export const canTransitionRepairRequest = (
  currentStatus: RepairStatus,
  nextStatus: RepairStatus,
  actor: RepairRequestWorkflowActor
): boolean =>
  getActionsForStatus(currentStatus).some(
    (action) => action.actor === actor && action.nextStatus === nextStatus
  );

export const getTransitionAction = (
  currentStatus: RepairStatus,
  nextStatus: RepairStatus,
  actor: RepairRequestWorkflowActor
): RepairRequestWorkflowAction | null =>
  getActionsForStatus(currentStatus).find(
    (action) => action.actor === actor && action.nextStatus === nextStatus
  ) || null;

export const getActiveRepairStatuses = (): RepairStatus[] =>
  REPAIR_STATUS_ORDER.filter((status) => isActiveRepairRequestStatus(status));

const getTimestampMs = (value: any): number => {
  if (!value) return 0;
  if (typeof value.toDate === 'function') {
    return value.toDate().getTime();
  }
  if (value instanceof Date) {
    return value.getTime();
  }
  return 0;
};

const getLastActivityMs = (request: RepairRequest): number =>
  getTimestampMs(request.updatedAt) || getTimestampMs(request.createdAt);

const sortByLastActivityDesc = (requests: RepairRequest[]): RepairRequest[] =>
  [...requests].sort((a, b) => getLastActivityMs(b) - getLastActivityMs(a));

const sortByLastActivityAsc = (requests: RepairRequest[]): RepairRequest[] =>
  [...requests].sort((a, b) => getLastActivityMs(a) - getLastActivityMs(b));

export const getServiceDashboardBucket = (
  requestOrStatus: RepairRequest | RepairStatus
): ServiceDashboardBucket => {
  const status =
    typeof requestOrStatus === 'string'
      ? requestOrStatus
      : (requestOrStatus.status as RepairStatus);
  return getRepairStatusMatrix(status).serviceDashboardBucket;
};

export const getServiceStatusMeta = (
  requestOrStatus: RepairRequest | RepairStatus
): ServiceDashboardStatusMeta => {
  const status =
    typeof requestOrStatus === 'string'
      ? requestOrStatus
      : (requestOrStatus.status as RepairStatus);
  const statusMatrix = getRepairStatusMatrix(status);
  const bucket = statusMatrix.serviceDashboardBucket;
  const isCancelled = statusMatrix.isCancelledHistory;

  if (bucket === 'history') {
    return {
      status,
      statusLabel: statusMatrix.label,
      bucket,
      bucketLabel: 'Historia',
      tone: statusMatrix.serviceDashboardTone,
      isHistory: true,
      isCancelled,
    };
  }

  return {
    status,
    statusLabel: statusMatrix.label,
    bucket,
    bucketLabel: bucket === 'reaction' ? 'Do reakcji' : 'W toku / Oczekujące',
    tone: statusMatrix.serviceDashboardTone,
    isHistory: false,
    isCancelled: false,
  };
};

export const getServiceDashboardOverview = (
  requests: RepairRequest[],
  options?: {
    staleHours?: number;
    now?: Date;
  }
): ServiceDashboardOverview => {
  const now = options?.now ?? new Date();
  const staleHours = options?.staleHours ?? 48;

  const grouped: Record<ServiceDashboardBucket, RepairRequest[]> = {
    reaction: [],
    inProgress: [],
    history: [],
  };

  requests.forEach((request) => {
    grouped[getServiceDashboardBucket(request)].push(request);
  });

  const reaction = sortByLastActivityAsc(grouped.reaction);
  const inProgress = sortByLastActivityAsc(grouped.inProgress);
  const history = sortByLastActivityDesc(grouped.history);
  const active = [...reaction, ...inProgress];
  const staleActive = active.filter((request) => isStaleRepairRequest(request, staleHours, now));

  return {
    active,
    history,
    reaction,
    inProgress,
    staleActive,
    counts: {
      reaction: reaction.length,
      inProgress: inProgress.length,
      history: history.length,
    },
  };
};

const concatPriorityBuckets = <K extends string>(
  priority: readonly K[],
  buckets: Record<K, RepairRequest[]>
): RepairRequest[] => priority.flatMap((bucket) => buckets[bucket]);

export const getClientRequestListBucket = (status: RepairStatus): ClientRequestListBucket => {
  if (isTerminalRepairRequestStatus(status)) {
    return 'completed';
  }
  if (requiresClientAction(status)) {
    return 'needsAction';
  }
  if (status === RepairStatus.NEW || status === RepairStatus.PENDING_QUOTE) {
    return 'waitingForService';
  }
  return 'active';
};

export const getClientRequestBuckets = (requests: RepairRequest[]): ClientRequestBuckets => {
  const grouped: Record<ClientRequestListBucket, RepairRequest[]> = {
    needsAction: [],
    active: [],
    waitingForService: [],
    completed: [],
  };

  requests.forEach((request) => {
    grouped[getClientRequestListBucket(request.status)].push(request);
  });

  const sorted: Record<ClientRequestListBucket, RepairRequest[]> = {
    needsAction: sortByLastActivityDesc(grouped.needsAction),
    active: sortByLastActivityDesc(grouped.active),
    waitingForService: sortByLastActivityDesc(grouped.waitingForService),
    completed: sortByLastActivityDesc(grouped.completed),
  };

  return {
    all: concatPriorityBuckets(CLIENT_LIST_PRIORITY, sorted),
    needsAction: sorted.needsAction,
    active: concatPriorityBuckets(
      CLIENT_LIST_PRIORITY.filter((bucket) => bucket !== 'completed'),
      sorted
    ),
    waitingForService: sorted.waitingForService,
    completed: sorted.completed,
  };
};

export const isWaitingForClient = (status: RepairStatus): boolean =>
  !isTerminalRepairRequestStatus(status) &&
  getRepairStatusMatrix(status).nextActionOwner === 'client';

const getServiceRequestListBucket = (status: RepairStatus): ServiceRequestListBucket => {
  const matrix = getRepairStatusMatrix(status);
  if (matrix.isTerminal) {
    return 'completed';
  }
  if (status === RepairStatus.NEW) {
    return 'newRequests';
  }
  if (status === RepairStatus.IN_PROGRESS) {
    return 'inProgress';
  }
  if (matrix.nextActionOwner === 'client') {
    return 'waitingForClient';
  }
  if (matrix.nextActionOwner === 'service') {
    return 'requiresServiceAction';
  }
  return 'inProgress';
};

export const isRecentlyCompleted = (
  request: RepairRequest,
  days = 14,
  now = new Date()
): boolean => {
  if (!isTerminalRepairRequestStatus(request.status)) return false;
  const referenceMs =
    getTimestampMs(request.completedDate) ||
    getTimestampMs(request.updatedAt) ||
    getTimestampMs(request.createdAt);
  if (!referenceMs) return false;
  const thresholdMs = now.getTime() - days * 24 * 60 * 60 * 1000;
  return referenceMs >= thresholdMs;
};

export const isStaleRepairRequest = (
  request: RepairRequest,
  staleHours = 48,
  now = new Date()
): boolean => {
  if (!isActiveRepairRequestStatus(request.status)) return false;
  const referenceMs = getLastActivityMs(request);
  if (!referenceMs) return false;
  const thresholdMs = now.getTime() - staleHours * 60 * 60 * 1000;
  return referenceMs <= thresholdMs;
};

export const getServiceDashboardBuckets = (
  requests: RepairRequest[],
  options?: {
    recentlyCompletedDays?: number;
    staleHours?: number;
    now?: Date;
  }
): ServiceDashboardBuckets => {
  const now = options?.now ?? new Date();
  const recentlyCompletedDays = options?.recentlyCompletedDays ?? 14;
  const staleHours = options?.staleHours ?? 48;

  const grouped: Record<ServiceRequestListBucket, RepairRequest[]> = {
    requiresServiceAction: [],
    newRequests: [],
    inProgress: [],
    waitingForClient: [],
    completed: [],
  };

  requests.forEach((request) => {
    grouped[getServiceRequestListBucket(request.status)].push(request);
  });

  const sorted: Record<ServiceRequestListBucket, RepairRequest[]> = {
    requiresServiceAction: sortByLastActivityDesc(grouped.requiresServiceAction),
    newRequests: sortByLastActivityDesc(grouped.newRequests),
    inProgress: sortByLastActivityDesc(grouped.inProgress),
    waitingForClient: sortByLastActivityDesc(grouped.waitingForClient),
    completed: sortByLastActivityDesc(grouped.completed),
  };

  const all = concatPriorityBuckets(SERVICE_LIST_PRIORITY, sorted);
  const active = all.filter((request) => isActiveRepairRequestStatus(request.status));
  const recentlyCompleted = sorted.completed.filter((request) =>
    isRecentlyCompleted(request, recentlyCompletedDays, now)
  );
  const stale = active.filter((request) => isStaleRepairRequest(request, staleHours, now));

  return {
    all,
    requiresServiceAction: sorted.requiresServiceAction,
    newRequests: sorted.newRequests,
    waitingForClient: sorted.waitingForClient,
    inProgress: sorted.inProgress,
    completed: sorted.completed,
    recentlyCompleted: sortByLastActivityDesc(recentlyCompleted),
    stale: sortByLastActivityDesc(stale),
    active,
  };
};

export const getRepairStatusTranslations = (): Record<RepairStatus, string> =>
  REPAIR_STATUS_ORDER.reduce(
    (acc, status) => ({ ...acc, [status]: getRepairRequestStatusLabel(status) }),
    {} as Record<RepairStatus, string>
  );
